import React, { useMemo } from 'react';
import { Paper, Stack, Typography, Skeleton } from '@mui/material';
import { Analytics } from '@mui/icons-material';
import LabelTooltip from '@/shared/components/LabelTooltip';
import { StyledIBMTypography } from '@/shared/orderTable/util';
import { formatQty } from '@/util';

export default function MarketMakerPreTradeAnalytics({
  amount,
  estimatedFees,
  isSpot,
  exposureTolerance,
  availableMargin,
  leverage,
  isLoading = false,
  mode = 'normal', // Mode determines slippage: aggressive (10bps), normal (3bps), passive (1bps)
  directionalBias = 0,
}) {
  const numericAmount = useMemo(() => {
    const n = parseFloat(String(amount || '').replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }, [amount]);

  const currentLeverage = leverage?.leverage || null;

  // Dynamic safety buffer based on mode: aggressive (2x), normal (1x), passive (0.5x)
  const safetyBuffer = useMemo(() => {
    const safetyBufferByMode = {
      aggressive: 2.0,
      normal: 1.0,
      passive: 0.5,
    };
    return safetyBufferByMode[mode] || 1.0; // Default to normal (1x)
  }, [mode]);

  // Dynamic slippage based on mode: aggressive (10bps), normal (3bps), passive (1bps)
  const slippageBps = useMemo(() => {
    const slippageBpsByMode = {
      aggressive: 10,
      normal: 3,
      passive: 1,
    };
    return slippageBpsByMode[mode] || 3; // Default to normal (3bps)
  }, [mode]);

  // Calculate slippage cost based on mode
  const slippageCost = useMemo(() => {
    if (!numericAmount) return null;
    return numericAmount * (slippageBps / 10000);
  }, [numericAmount, slippageBps]);

  // Calculate total estimated fees range (fee budget ± slippage)
  const totalEstimatedFeesRange = useMemo(() => {
    const numericFees = parseFloat(String(estimatedFees || '').replace(/,/g, ''));
    if (!numericFees || !slippageCost) return null;
    return {
      min: numericFees - slippageCost,
      max: numericFees + slippageCost,
    };
  }, [estimatedFees, slippageCost]);

  const recommendedMargin = useMemo(() => {
    if (!numericAmount) return null;

    let baseMargin;

    // Spot: exposure * notional target
    if (isSpot) {
      const tol = Number(exposureTolerance ?? 0.06);
      baseMargin = numericAmount * tol;
    } else {
      // Derivatives: notional / leverage with safety buffer
      if (!currentLeverage) return null;
      baseMargin = (numericAmount / currentLeverage) * safetyBuffer;
    }

    // Apply directional bias adjustment: up to 20% more margin for extreme bias
    // At -1 or 1 bias: 20% more margin, at 0 bias: no change
    const biasAdjustment = Math.abs(directionalBias) * 0.2; // 0 to 0.2 multiplier
    const adjustedMargin = baseMargin * (1 + biasAdjustment);

    return adjustedMargin;
  }, [numericAmount, currentLeverage, isSpot, exposureTolerance, safetyBuffer, directionalBias]);

  const recommendedMarginColor = useMemo(() => {
    if (!recommendedMargin || availableMargin == null) return 'text.secondary';

    if (recommendedMargin < availableMargin) {
      return 'success.main'; // Green
    }
    if (recommendedMargin <= availableMargin * 5) {
      return 'warning.main'; // Orange
    }
    return 'error.main'; // Red
  }, [recommendedMargin, availableMargin]);

  return (
    <Paper elevation={1} sx={{ pt: 1, pb: 1, px: 2, backgroundColor: 'transparent', backdropFilter: 'blur(5px)' }}>
      <Stack direction='column' spacing={1}>
        <Stack alignItems='center' direction='row' spacing={1}>
          <Analytics sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          <Typography variant='body1'>Pre-Trade Analytics</Typography>
        </Stack>

        <Stack direction='column' spacing={1}>
          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Available Margin'
              link='https://docs.tread.fi/market-maker-bot#pre-trade-analytics'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>
                    Quote-asset funds currently available to open new positions.
                  </Typography>
                  <Typography color='text.secondary' variant='body2'>
                    Falls back to wallet balance when margin balance is unavailable.
                  </Typography>
                </div>
              }
            />
            {isLoading ? (
              <Skeleton height={20} variant='rectangular' width={80} />
            ) : (
              <StyledIBMTypography
                color={availableMargin != null ? 'text.primary' : 'text.secondary'}
                style={{ display: 'inline' }}
                variant='body2'
              >
                {availableMargin != null ? formatQty(availableMargin, true) : '-'}
              </StyledIBMTypography>
            )}
          </Stack>

          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Recommended Margin'
              link='https://docs.tread.fi/market-maker-bot#pre-trade-analytics'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>
                    Heuristic: notional ÷ leverage × {safetyBuffer.toFixed(2)} buffer. Adjust if liquidation buffer is
                    low.
                  </Typography>
                </div>
              }
            />
            {isLoading ? (
              <Skeleton height={20} variant='rectangular' width={80} />
            ) : (
              <StyledIBMTypography color={recommendedMarginColor} style={{ display: 'inline' }}>
                {recommendedMargin ? formatQty(recommendedMargin, true) : '-'}
              </StyledIBMTypography>
            )}
          </Stack>

          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Exchange Fees'
              link='https://docs.tread.fi/market-maker-bot#pre-trade-analytics'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>Budget for builder + maker fees.</Typography>
                </div>
              }
            />
            {isLoading ? (
              <Skeleton height={20} variant='rectangular' width={80} />
            ) : (
              <StyledIBMTypography
                color={estimatedFees ? 'text.primary' : 'text.secondary'}
                style={{ display: 'inline' }}
              >
                {estimatedFees
                  ? (() => {
                      const value = parseFloat(String(estimatedFees).replace(/,/g, ''));
                      return value < 0 ? `-$${Math.abs(value).toFixed(2)}` : `$${value.toFixed(2)}`;
                    })()
                  : '-'}
              </StyledIBMTypography>
            )}
          </Stack>

          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='PnL Est.'
              link='https://docs.tread.fi/market-maker-bot#pre-trade-analytics'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>
                    Conservative estimate of {slippageBps} basis points slippage cost.
                  </Typography>
                </div>
              }
            />
            {isLoading ? (
              <Skeleton height={20} variant='rectangular' width={80} />
            ) : (
              <StyledIBMTypography
                color={slippageCost ? 'text.primary' : 'text.secondary'}
                style={{ display: 'inline' }}
              >
                {slippageCost ? `±$${slippageCost.toFixed(2)}` : '-'}
              </StyledIBMTypography>
            )}
          </Stack>

          <Stack direction='row' sx={{ justifyContent: 'space-between' }}>
            <LabelTooltip
              label='Total Est. Fees'
              link='https://docs.tread.fi/market-maker-bot#pre-trade-analytics'
              placement='left'
              title={
                <div>
                  <Typography sx={{ marginBottom: 1.5 }}>
                    Total estimated fees including exchange fees and slippage.
                  </Typography>
                </div>
              }
            />
            {isLoading ? (
              <Skeleton height={20} variant='rectangular' width={100} />
            ) : (
              <StyledIBMTypography
                color={totalEstimatedFeesRange ? 'text.primary' : 'text.secondary'}
                style={{ display: 'inline' }}
                variant='body2'
              >
                {totalEstimatedFeesRange
                  ? (() => {
                      const minFormatted =
                        totalEstimatedFeesRange.min < 0
                          ? `-$${Math.abs(totalEstimatedFeesRange.min).toFixed(2)}`
                          : `$${totalEstimatedFeesRange.min.toFixed(2)}`;
                      const maxFormatted =
                        totalEstimatedFeesRange.max < 0
                          ? `-$${Math.abs(totalEstimatedFeesRange.max).toFixed(2)}`
                          : `$${totalEstimatedFeesRange.max.toFixed(2)}`;
                      return `${minFormatted} - ${maxFormatted}`;
                    })()
                  : '-'}
              </StyledIBMTypography>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
}
